// DESCRIPTION: Pure attendee details form component for booking flows.
// Renders name, email, and phone fields with validation display.

import React from 'react';
import { __ } from '@wordpress/i18n';

/**
 * AttendeeForm — a pure, reusable form for collecting attendee details.
 *
 * @param {Object}   props
 * @param {Object}   props.formData    Form values: { attendee_name, attendee_email, attendee_phone }.
 * @param {Object}   props.errors      Validation errors keyed by field name.
 * @param {Function} props.onChange     Called with the synthetic event when any field changes.
 * @param {boolean}  [props.disabled=false] Disable all inputs (e.g., during submission).
 * @param {string}   [props.className='']   Additional CSS class names.
 */
const AttendeeForm = ( {
    formData,
    errors = {},
    onChange,
    disabled = false,
    className = '',
} ) => {
    return (
        <div className={ `nb-attendee-form ${ className }`.trim() }>
            {/* Full Name */}
            <div className="nb-attendee-form__field">
                <label htmlFor="attendee_name" className="nb-attendee-form__label">
                    { __( 'Full Name', 'nemtly-booking' ) }
                    <span className="nb-attendee-form__required">*</span>
                </label>
                <input
                    type="text"
                    id="attendee_name"
                    name="attendee_name"
                    value={ formData.attendee_name || '' }
                    onChange={ onChange }
                    className={ `nb-attendee-form__input ${ errors.attendee_name ? 'nb-attendee-form__input--error' : '' }` }
                    placeholder={ __( 'Enter your full name', 'nemtly-booking' ) }
                    required
                    disabled={ disabled }
                />
                { errors.attendee_name && (
                    <p className="nb-attendee-form__error">
                        { errors.attendee_name }
                    </p>
                ) }
            </div>

            {/* Email Address */}
            <div className="nb-attendee-form__field">
                <label htmlFor="attendee_email" className="nb-attendee-form__label">
                    { __( 'Email Address', 'nemtly-booking' ) }
                    <span className="nb-attendee-form__required">*</span>
                </label>
                <input
                    type="email"
                    id="attendee_email"
                    name="attendee_email"
                    value={ formData.attendee_email || '' }
                    onChange={ onChange }
                    className={ `nb-attendee-form__input ${ errors.attendee_email ? 'nb-attendee-form__input--error' : '' }` }
                    placeholder={ __( 'name@example.com', 'nemtly-booking' ) }
                    required
                    disabled={ disabled }
                />
                { errors.attendee_email && (
                    <p className="nb-attendee-form__error">
                        { errors.attendee_email }
                    </p>
                ) }
            </div>

            {/* Phone Number (Optional) */}
            <div className="nb-attendee-form__field">
                <label htmlFor="attendee_phone" className="nb-attendee-form__label">
                    { __( 'Phone Number', 'nemtly-booking' ) }
                    <span className="nb-attendee-form__optional">
                        { __( '(Optional)', 'nemtly-booking' ) }
                    </span>
                </label>
                <input
                    type="tel"
                    id="attendee_phone"
                    name="attendee_phone"
                    value={ formData.attendee_phone || '' }
                    onChange={ onChange }
                    className={ `nb-attendee-form__input ${ errors.attendee_phone ? 'nb-attendee-form__input--error' : '' }` }
                    placeholder={ __( '+1 (555) 000-0000', 'nemtly-booking' ) }
                    disabled={ disabled }
                />
                { errors.attendee_phone && (
                    <p className="nb-attendee-form__error">
                        { errors.attendee_phone }
                    </p>
                ) }
            </div>
        </div>
    );
};

export default AttendeeForm;
